import { useMutation, useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export const APPLICATION_STATUSES = [
  "to_apply",
  "applied",
  "interview",
  "offer",
  "rejected",
] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export type Application = {
  id: string;
  jobTitle: string;
  company: string;
  location: string | null;
  url: string | null;
  jobId: string | null;
  resumeId: string | null;
  analysisId: string | null;
  status: ApplicationStatus;
  notes: string | null;
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type NewApplication = {
  jobTitle: string;
  company: string;
  location?: string;
  url?: string;
  jobId?: string;
  resumeId?: string;
  analysisId?: string;
  status?: ApplicationStatus;
};

const invalidate = () => queryClient.invalidateQueries({ queryKey: ["applications"] });

export const fetchApplications = async () => {
  const response = await axios.get<Application[]>("/applications");
  return response.data;
};

export const useApplications = () => {
  const { data, isLoading } = useQuery({ queryKey: ["applications"], queryFn: fetchApplications });
  return { applications: data, loading: isLoading };
};

export type ApplicationStats = {
  total: number;
  funnel: { toApply: number; applied: number; interview: number; offer: number; rejected: number };
  sent: number;
  callbackRate: number;
  offerRate: number;
  byResume: {
    resumeId: string | null;
    title: string;
    sent: number;
    interviews: number;
    offers: number;
    callbackRate: number;
  }[];
};

export const fetchApplicationStats = async () => {
  const response = await axios.get<ApplicationStats>("/applications/stats");
  return response.data;
};

export const useApplicationStats = () => {
  const { data, isPending } = useQuery({
    queryKey: ["applications", "stats"],
    queryFn: fetchApplicationStats,
  });
  return { stats: data, loading: isPending };
};

export const createApplication = async (payload: NewApplication) => {
  const response = await axios.post<Application>("/applications", payload);
  return response.data;
};

export const useCreateApplication = () => {
  const { mutateAsync: create, isPending: loading } = useMutation({
    mutationFn: createApplication,
    onSuccess: () => void invalidate(),
  });
  return { create, loading };
};

export const updateApplication = async (payload: {
  id: string;
  status?: ApplicationStatus;
  notes?: string;
}) => {
  const response = await axios.patch<Application>(`/applications/${payload.id}`, {
    status: payload.status,
    notes: payload.notes,
  });
  return response.data;
};

export const useUpdateApplication = () => {
  const { mutateAsync: update } = useMutation({
    mutationFn: updateApplication,
    onSuccess: () => void invalidate(),
  });
  return { update };
};

export const deleteApplication = async (id: string) => {
  await axios.delete(`/applications/${id}`);
  return id;
};

export const useDeleteApplication = () => {
  const { mutateAsync: remove } = useMutation({
    mutationFn: deleteApplication,
    onSuccess: (id) => {
      queryClient.setQueryData<Application[]>(["applications"], (cache) =>
        cache?.filter((application) => application.id !== id),
      );
    },
  });
  return { remove };
};
